/**
 * Tiffany (global) specific configuration.
 *
 * This will be merged with `src/config/index.ts`
 */

import customerDataForm from "./forms/customerData";
import customerConsentForm from "./forms/customerConsent";
import customerAdditionalInfo from "./forms/customerAdditionalInfo";
import advisorForm from "./forms/advisorForm";
import {
  fromDDCCustomer,
  toDDCCustomer,
  processApiCustomerResponse,
  processApiCustomerResponseJapan,
  fromDDCCustomerJapan
} from "./transforms";
import {
  CUSTOMER_DETAILS_COLUMNS,
  CUSTOMER_DETAILS_COLUMNS_JAPAN,
  //MARITAL_STATUS_OPTIONS,
  RESULT_FIELDS,
  RESULT_FIELDS_JAPAN
} from "./fields";

const AUTH_BASE_URL = "https://tiffany.okta.com";
const API_PATH = "";
const stage: string = process.env.REACT_APP_STAGE || "dev";
const [BACKEND_BASE_URL, AUTH_CLIENT_ID, DQE_ENDPOINT, AUTH_BASE_PATH] =
  stage === "local"
    ? [
        "http://localhost:5000",
        "0oa3n9ll7pdEqF1Mq417",
        "https://preprod.dqe-software.com/SINGLEV2/?Pays={{country}}&Licence=I8s7w2Yn-FSmxoTd.echAxacry&Adresse={{search}}",
        "/oauth2/aus3n9nzthg4sSMOq417/v1"
      ]
    : stage === "dev" // localhost or dev
    ? [
        "https://aue2-dev-ddc-appsvc.azurewebsites.net",
        "0oa3n9ll7pdEqF1Mq417",
        "https://preprod.dqe-software.com/SINGLEV2/?Pays={{country}}&Licence=I8s7w2Yn-FSmxoTd.echAxacry&Adresse={{search}}",
        "/oauth2/aus3n9nzthg4sSMOq417/v1"
      ]
    : stage === "qa"
    ? [
        "https://aue2-qa-ddc-appsvc.azurewebsites.net",
        "0oa3n9ll7pdEqF1Mq417",
        "https://preprod.dqe-software.com/SINGLEV2/?Pays={{country}}&Licence=I8s7w2Yn-FSmxoTd.echAxacry&Adresse={{search}}",
        "/oauth2/aus3n9nzthg4sSMOq417/v1"
      ]
    : stage === "staging" || stage === "sta"
    ? [
        "https://aue2-stage-ddc-appsvc.azurewebsites.net",
        "0oa4ccue1oHzRwFln417",
        "https://preprod.dqe-software.com/SINGLEV2/?Pays={{country}}&Licence=I8s7w2Yn-FSmxoTd.echAxacry&Adresse={{search}}",
        "/oauth2/aus3n9nzthg4sSMOq417/v1"
      ]
    : [
        // prod
        "https://aue2-prod-ddc-appsvc.azurewebsites.net",
        "0oa4mtcthv8gKXzO5417",
        "https://prod2.dqe-software.com/SINGLEV2/?Pays={{country}}&Licence=I8s7w2Yn-FSmxoTd.echAxacry&Adresse={{search}}",
        "/oauth2/default/v1"
      ];
const ALL_LANGUAGES = [
  "en",
  "zh-Hans",
  "zh-Hant",
  "ru",
  "nl",
  "th",
  "de",
  "cs",
  "es",
  "ko",
  "da",
  "fl",
  "es-MX",
  "pt",
  "it",
  "fr",
  "ja"
];

export default {
  id: "tiffany",
  api: "/ddc/v0",
  defaultZone: "usa",
  languages: {
    supportedLanguages: ALL_LANGUAGES,
    nonRomanLanguages: [],
    loadPath: "", // BACKEND_BASE_URL + "/translations/{{lng}}/{{ns}}",
    sort: false
  },

  services: {
    oidc: {
      // Okta Oidc documentation: https://developer.okta.com/docs/reference/api/oidc
      // does not support refresh token from browser
      baseUrl: AUTH_BASE_URL,
      loginRedirectEndpoint: `${AUTH_BASE_PATH}/authorize`,
      scope: "openid offline_access",
      getTokenEndpoint: `${AUTH_BASE_PATH}/token`,
      logoutRedirectEndpoint: `${AUTH_BASE_PATH}/logout`,
      clientId: AUTH_CLIENT_ID,
      codeChallenge: "HPyLLwWqLbWJV0Ks7fArAD1TEGtV6JI2P8aG1EaVdjY",
      codeChallengeMethod: "S256",
      codeVerifier:
        "swbBEq~75oJQVAdgJEND6n7HNf5seQJIOuqwNE8qt33JOIbAYzsqOY.YmV_~1l_.nViF4f0xSiy87IsMh52zkHXwigneHj5FP2wcgUC0te~imSlRXsYrf4j1fH9_MwqJ"
    },
    api: {
      translationsEndpoint: "/translations/{{lng}}/{{ns}}",
      baseUrl: BACKEND_BASE_URL,
      key: "a0d469fa10d149179e0c058a51c99bac", //Ocp-Apim-Subscription-Key for process API
      getConfigEndpoint: API_PATH + "/ddcconfig",
      searchCustomersEndpoint: API_PATH + "/customer?search=",
      verifyAdvisorEndpoint: API_PATH + "/advisor",
      customerEndpoint: API_PATH + "/customer",
      getCustomersHistoryEndpoint: API_PATH + "/customer/history",
      customerStatusEndpoint: API_PATH + "/customer?hashcode=",
      dqeAddressesEndpoint: DQE_ENDPOINT
    }
  },

  zones: {
    usa: {
      disabled: true, // override by backend config
      supportedLanguages: ALL_LANGUAGES
    },
    jpn: {
      disabled: true, // override by backend config
      supportedLanguages: [
        "ja",
        "en",
        "zh-Hans",
        "zh-Hant",
        "ru",
        "nl",
        "th",
        "de",
        "cs",
        "es",
        "ko",
        "da",
        "fl",
        "es-MX",
        "pt",
        "it",
        "fr"
      ],
      config: {
        customise: {
          customer: {
            enableRemoteCustomer: false,
            summaryFields: CUSTOMER_DETAILS_COLUMNS_JAPAN
          },
          searchCustomer: {
            resultsFields: RESULT_FIELDS_JAPAN,
            detailsColumns: CUSTOMER_DETAILS_COLUMNS_JAPAN
          },
          history: {
            enable: false,
            resultsFields: RESULT_FIELDS_JAPAN,
            detailsColumns: CUSTOMER_DETAILS_COLUMNS_JAPAN,
            enableEdit: true
          }
        },
        transforms: {
          customer: {
            read: fromDDCCustomerJapan,
            write: toDDCCustomer,
            response: processApiCustomerResponseJapan
          }
        }
      }
    },
    aus: {
      disabled: true, // override by backend config
      supportedLanguages: ["en", "zh-Hans"]
    },
    aut: {
      disabled: true, // override by backend config
      supportedLanguages: ["de", "en", "zh-Hans"]
    },
    bel: {
      disabled: true, // override by backend config
      supportedLanguages: ["fr", "en", "zh-Hans"]
    },
    cze: {
      disabled: true, // override by backend config
      supportedLanguages: ["cs", "en", "zh-Hans"]
    },
    can: {
      disabled: true, // override by backend config
      supportedLanguages: ["en", "fr"]
    },
    chn: {
      disabled: true, // override by backend config
      supportedLanguages: ["zh-Hans", "en"]
    },
    chl: {
      disabled: true, // override by backend config
      supportedLanguages: ["es", "en", "zh-Hans"]
    },
    dnk: {
      disabled: true, // override by backend config
      supportedLanguages: ["en", "da", "de", "zh-Hans"]
    },
    deu: {
      disabled: true, // override by backend config
      supportedLanguages: ["de", "en", "zh-Hans", "it", "ru"]
    },
    fra: {
      disabled: true, // override by backend config
      supportedLanguages: ["fr", "en", "zh-Hans"]
    },
    hkg: {
      disabled: true, // override by backend config
      supportedLanguages: ["en", "zh-Hans", "zh-Hant"]
    },
    irl: {
      disabled: true, // override by backend config
      supportedLanguages: ["en", "zh-Hans"]
    },
    ita: {
      disabled: true, // override by backend config
      supportedLanguages: ["it", "en", "ru", "es", "zh-Hans"]
    },
    sau: {
      disabled: true, // override by backend config
      supportedLanguages: ["en"]
    },
    nzl: {
      disabled: true, // override by backend config
      supportedLanguages: ["en", "zh-Hans"]
    },
    kor: {
      disabled: true, // override by backend config
      supportedLanguages: ["ko", "zh-Hans", "en"]
    },
    kwt: {
      disabled: true, // override by backend config
      supportedLanguages: ["en"]
    },
    mac: {
      disabled: true, // override by backend config
      supportedLanguages: ["en", "zh-Hans", "zh-Hant"]
    },
    mys: {
      disabled: true, // override by backend config
      supportedLanguages: ["en", "zh-Hans"]
    },
    mex: {
      disabled: true, // override by backend config
      supportedLanguages: ["es-MX", "en", "fr", "zh-Hans"]
    },
    nld: {
      disabled: true, // override by backend config
      supportedLanguages: ["en", "nl", "zh-Hans"]
    },
    phl: {
      disabled: true, // override by backend config
      supportedLanguages: ["en"]
    },
    sgp: {
      disabled: true, // override by backend config
      supportedLanguages: ["en", "zh-Hans"]
    },
    esp: {
      disabled: true, // override by backend config
      supportedLanguages: ["es", "en", "ru", "zh-Hans"]
    },
    swe: {
      disabled: true, // override by backend config
      supportedLanguages: ["en"]
    },
    che: {
      disabled: true, // override by backend config
      supportedLanguages: ["en", "de", "fr", "zh-Hans"]
    },
    twn: {
      disabled: true, // override by backend config
      supportedLanguages: ["zh-Hans", "en", "zh-Hans", "ja"]
    },
    tha: {
      disabled: true, // override by backend config
      supportedLanguages: ["en", "th", "zh-Hans"]
    },
    are: {
      disabled: true, // override by backend config
      supportedLanguages: ["en", "ru", "zh-Hans"]
    },
    gbr: {
      disabled: true, // override by backend config
      supportedLanguages: ["en", "zh-Hans", "it", "ru"]
    },
    vnm: {
      disabled: true, // override by backend config
      supportedLanguages: ["en"]
    }
  }, // received from ddcconfig in backend
  customise: {
    header: { showNotifications: false },
    languageSwitcher: {
      showOnlyZoneLanguages: true
    },
    verifySalesPerson: {
      resetOnActions: true
    },
    actions: {
      remoteCustomerButtonTheme: "primary"
    },
    searchCustomer: {
      enable: true,
      resultsFields: RESULT_FIELDS,
      detailsColumns: CUSTOMER_DETAILS_COLUMNS,
      enableEdit: true
    },
    customer: {
      enablePrefilled: true,
      enableAdditionalInfo: false,
      enableAdvisorForm: false,
      enableSummary: true,
      enableResultsModal: true,
      enableRemoteCustomer: false,
      summaryFields: CUSTOMER_DETAILS_COLUMNS,
      prefillValues: {
        phoneType: "3",
        calendarType: "1"
      }
    },
    history: {
      enable: false
    },
    fields: {
      markRequiredWithSymbol: "*"
      // get from backend
      /*picklistValues: [
        {
          name: "maritalStatus",
          options: MARITAL_STATUS_OPTIONS
        },
        {
          name: "nationality",
          options: ...
        }
      ]*/
    },
    notifications: {
      displayPartialBody: true
    },
    setupPage: {
      filterStoresByZone: true
    }
  },
  forms: {
    schema: "form/customerFormDefinitions.json",
    fields: [
      "form/customerDataFields.json",
      "form/customerConsentFields.json",
      "form/customerThanks.json",
      "form/additionalInfoFields.json"
    ],
    customer: [
      customerDataForm,
      customerConsentForm,
      customerAdditionalInfo,
      advisorForm
    ]
  },
  transforms: {
    customer: {
      read: fromDDCCustomer,
      write: toDDCCustomer,
      response: processApiCustomerResponse
    }
  }
};
